import { type Mode } from '@libraries/typescript/api_client';

type NotificationResourceRef = {
    readonly type: string;
    readonly id: string;
};

type NotificationNavigationSource = {
    readonly resource: NotificationResourceRef;
    readonly relationships: ReadonlyArray<NotificationResourceRef>;
};

type NotificationNavigationTarget =
    | {
          readonly kind: 'home';
      }
    | {
          readonly kind: 'conversation';
          readonly conversationId: string;
      }
    | {
          readonly kind: 'message_outreach';
          readonly messageOutreachId: string;
      }
    | {
          readonly kind: 'voice_call';
          readonly voiceCallId: string;
      }
    | {
          readonly kind: 'shift';
          readonly shiftId: string;
      }
    | {
          readonly kind: 'outreach';
          readonly outreachId: string;
      }
    | {
          readonly kind: 'caregiver';
          readonly caregiverId: string;
      }
    | {
          readonly kind: 'client';
          readonly clientId: string;
      }
    | {
          readonly kind: 'agent_conversation';
          readonly agentConversationId: string;
          readonly agentRunId: string | null;
      }
    | {
          readonly kind: 'notification_settings';
          readonly highlight?: 'auto-cleanup';
      };

function findRelatedResource(
    source: NotificationNavigationSource,
    resourceTypes: ReadonlyArray<string>,
): NotificationResourceRef | null {
    const match = source.relationships.find((relationship) =>
        resourceTypes.includes(relationship.type),
    );

    return match ?? null;
}

export function getNotificationNavigationTarget(
    source: NotificationNavigationSource,
): NotificationNavigationTarget | null {
    // Notices about the user's own settings land on the notification
    // settings page. The only user_settings producer today is the
    // auto-disable notice, so the section highlight is decided here at
    // interpretation; the route builders below just serialize whatever
    // the target carries.
    if (source.resource.type === 'user_settings') {
        return { kind: 'notification_settings', highlight: 'auto-cleanup' };
    }

    if (source.resource.type === 'activity_event') {
        const conversationRef = findRelatedResource(source, ['conversation']);
        if (conversationRef) {
            return {
                kind: 'conversation',
                conversationId: conversationRef.id,
            };
        }

        const messageOutreachRef = findRelatedResource(source, [
            'message_outreach',
        ]);
        if (messageOutreachRef) {
            return {
                kind: 'message_outreach',
                messageOutreachId: messageOutreachRef.id,
            };
        }

        const voiceCallRef = findRelatedResource(source, ['voice_call']);
        if (voiceCallRef) {
            return {
                kind: 'voice_call',
                voiceCallId: voiceCallRef.id,
            };
        }

        const shiftRef = findRelatedResource(source, ['shift']);
        if (shiftRef) {
            return {
                kind: 'shift',
                shiftId: shiftRef.id,
            };
        }

        const outreachRef = findRelatedResource(source, [
            'outreach',
            'callout',
        ]);
        if (outreachRef) {
            return {
                kind: 'outreach',
                outreachId: outreachRef.id,
            };
        }

        const caregiverRef = findRelatedResource(source, ['caregiver']);
        if (caregiverRef) {
            return {
                kind: 'caregiver',
                caregiverId: caregiverRef.id,
            };
        }

        const clientRef = findRelatedResource(source, ['client']);
        if (clientRef) {
            return {
                kind: 'client',
                clientId: clientRef.id,
            };
        }

        return { kind: 'home' };
    }

    if (source.resource.type === 'conversation') {
        return {
            kind: 'conversation',
            conversationId: source.resource.id,
        };
    }

    if (
        source.resource.type === 'message' ||
        source.resource.type === 'staged_message'
    ) {
        const conversationRef = findRelatedResource(source, ['conversation']);
        if (conversationRef) {
            return {
                kind: 'conversation',
                conversationId: conversationRef.id,
            };
        }
        return null;
    }

    if (source.resource.type === 'message_outreach') {
        return {
            kind: 'message_outreach',
            messageOutreachId: source.resource.id,
        };
    }

    if (source.resource.type === 'voice_call') {
        return {
            kind: 'voice_call',
            voiceCallId: source.resource.id,
        };
    }

    if (
        source.resource.type === 'outreach' ||
        source.resource.type === 'callout'
    ) {
        return {
            kind: 'outreach',
            outreachId: source.resource.id,
        };
    }

    // Agent-run notifications (e.g. tool-approval alerts) target the run's
    // agent chat via an explicit relationship. The run id itself is not a
    // conversation id, so without the relationship there is nothing to
    // navigate to.
    if (source.resource.type === 'agent_run') {
        const agentConversationRef = findRelatedResource(source, [
            'agent_conversation',
        ]);
        if (agentConversationRef) {
            return {
                kind: 'agent_conversation',
                agentConversationId: agentConversationRef.id,
                agentRunId: source.resource.id,
            };
        }
        return null;
    }

    if (source.resource.type === 'contact_attempt') {
        const outreachRef = findRelatedResource(source, [
            'outreach',
            'callout',
        ]);
        if (outreachRef) {
            return {
                kind: 'outreach',
                outreachId: outreachRef.id,
            };
        }
    }

    // The primary resource is the action target. A missed-clock shift also
    // carries its outreach as context, but opening that outreach can land on
    // an exhausted workflow instead of the shift that needs correction.
    if (source.resource.type === 'shift') {
        return {
            kind: 'shift',
            shiftId: source.resource.id,
        };
    }

    const conversationRef = findRelatedResource(source, ['conversation']);
    if (conversationRef) {
        return {
            kind: 'conversation',
            conversationId: conversationRef.id,
        };
    }

    const messageOutreachRef = findRelatedResource(source, [
        'message_outreach',
    ]);
    if (messageOutreachRef) {
        return {
            kind: 'message_outreach',
            messageOutreachId: messageOutreachRef.id,
        };
    }

    const outreachRef = findRelatedResource(source, ['outreach', 'callout']);
    if (outreachRef) {
        return {
            kind: 'outreach',
            outreachId: outreachRef.id,
        };
    }

    const voiceCallRef = findRelatedResource(source, ['voice_call']);
    if (voiceCallRef) {
        return {
            kind: 'voice_call',
            voiceCallId: voiceCallRef.id,
        };
    }

    const shiftRef = findRelatedResource(source, ['shift']);
    if (shiftRef) {
        return {
            kind: 'shift',
            shiftId: shiftRef.id,
        };
    }

    if (source.resource.type === 'caregiver') {
        return {
            kind: 'caregiver',
            caregiverId: source.resource.id,
        };
    }

    const caregiverRef = findRelatedResource(source, ['caregiver']);
    if (caregiverRef) {
        return {
            kind: 'caregiver',
            caregiverId: caregiverRef.id,
        };
    }

    if (source.resource.type === 'client') {
        return {
            kind: 'client',
            clientId: source.resource.id,
        };
    }

    const clientRef = findRelatedResource(source, ['client']);
    if (clientRef) {
        return {
            kind: 'client',
            clientId: clientRef.id,
        };
    }

    return null;
}

export function getNotificationNavigationRouteOptions(
    target: NotificationNavigationTarget,
    organizationId?: string | null,
    mode?: Mode | null,
) {
    switch (target.kind) {
        // In-tab clicks open the agent chat panel directly (see
        // use_organization_events); navigation falls back to home.
        case 'home':
        case 'agent_conversation':
            return {
                to: '/home',
                search: {
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        case 'conversation':
            return {
                to: '/conversations',
                search: {
                    conversationId: target.conversationId,
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        case 'message_outreach':
            return {
                to: '/outreaches/message/$message-id',
                params: { 'message-id': target.messageOutreachId },
                search: {
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        case 'voice_call':
            return {
                to: '/voice-calls',
                search: {
                    voiceCallId: target.voiceCallId,
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        case 'shift':
            return {
                to: '/shifts',
                search: {
                    shiftId: target.shiftId,
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        case 'outreach':
            return {
                to: '/outreaches/$callout-id',
                params: { 'callout-id': target.outreachId },
                search: {
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        case 'caregiver':
            return {
                to: '/caregivers',
                search: {
                    caregiverId: target.caregiverId,
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        case 'client':
            return {
                to: '/clients',
                search: {
                    clientId: target.clientId,
                    ...(organizationId ? { organizationId } : {}),
                    ...(mode ? { mode } : {}),
                },
            } as const;
        // The notification settings route only validates organizationId, so
        // no mode param here.
        case 'notification_settings':
            return {
                to: '/settings/notifications',
                search: {
                    ...(target.highlight
                        ? { highlight: target.highlight }
                        : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            } as const;
        default: {
            const exhaustiveCheck: never = target;
            return exhaustiveCheck;
        }
    }
}

export function buildNotificationNavigationUrl(
    target: NotificationNavigationTarget,
    organizationId?: string | null,
    mode?: Mode | null,
): string {
    switch (target.kind) {
        case 'home': {
            const searchParams = new URLSearchParams();
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }
            const queryString = searchParams.toString();
            return queryString ? `/home?${queryString}` : '/home';
        }
        case 'agent_conversation': {
            const searchParams = new URLSearchParams();
            if (target.agentRunId !== null) {
                if (organizationId) {
                    searchParams.set('organizationId', organizationId);
                }
                if (mode) {
                    searchParams.set('mode', mode);
                }
                const queryString = searchParams.toString();
                const encodedRunId = encodeURIComponent(target.agentRunId);
                const runPath = `/agent/runs/${encodedRunId}`;
                return queryString ? `${runPath}?${queryString}` : runPath;
            }

            searchParams.set('chatId', target.agentConversationId);
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }
            const queryString = searchParams.toString();
            return `/home?${queryString}`;
        }
        case 'conversation': {
            const searchParams = new URLSearchParams({
                conversationId: target.conversationId,
            });
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }

            return `/conversations?${searchParams.toString()}`;
        }
        case 'message_outreach': {
            const searchParams = new URLSearchParams();
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }

            const search = searchParams.toString();
            return `/outreaches/message/${target.messageOutreachId}${search ? `?${search}` : ''}`;
        }
        case 'voice_call': {
            const searchParams = new URLSearchParams({
                voiceCallId: target.voiceCallId,
            });
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }

            return `/voice-calls?${searchParams.toString()}`;
        }
        case 'shift': {
            const searchParams = new URLSearchParams({
                shiftId: target.shiftId,
            });
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }

            return `/shifts?${searchParams.toString()}`;
        }
        case 'outreach': {
            const searchParams = new URLSearchParams();
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }

            const search = searchParams.toString();
            return `/outreaches/${target.outreachId}${search ? `?${search}` : ''}`;
        }
        case 'caregiver': {
            const searchParams = new URLSearchParams({
                caregiverId: target.caregiverId,
            });
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }

            return `/caregivers?${searchParams.toString()}`;
        }
        case 'client': {
            const searchParams = new URLSearchParams({
                clientId: target.clientId,
            });
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }
            if (mode) {
                searchParams.set('mode', mode);
            }

            return `/clients?${searchParams.toString()}`;
        }
        case 'notification_settings': {
            const searchParams = new URLSearchParams();
            if (target.highlight) {
                searchParams.set('highlight', target.highlight);
            }
            if (organizationId) {
                searchParams.set('organizationId', organizationId);
            }

            const search = searchParams.toString();
            return `/settings/notifications${search ? `?${search}` : ''}`;
        }
        default: {
            const exhaustiveCheck: never = target;
            return exhaustiveCheck;
        }
    }
}

export type {
    NotificationNavigationSource,
    NotificationNavigationTarget,
    NotificationResourceRef,
};
